import type { PurchaseOrderListRow } from "@/modules/inventory/domain/types";
import type { PurchaseOrderStatus } from "@/server/platform/db/schema/purchase-orders";
import { formatDateLocal } from "@/shared/lib/date-time";
import { formatLakDisplay } from "@/shared/lib/format-number";
import { DataTable, createSortableColumn, TanstackReactTable } from "@/shared/ui/data-table";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { EyeIcon } from "lucide-react";
import { PurchaseOrderStatusBadge } from "./PurchaseOrderStatusBadge";

type PurchaseOrdersTableProps = {
  data: PurchaseOrderListRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function PurchaseOrdersTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
}: PurchaseOrdersTableProps) {
  const nav = useNavigate();

  const columns: TanstackReactTable.ColumnDef<PurchaseOrderListRow>[] = [
    createSortableColumn<PurchaseOrderListRow>("poNo", "ເລກ PO", {
      size: 140,
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.poNo}</span>
      ),
    }),
    createSortableColumn<PurchaseOrderListRow>("supplierName", "ຜູ້ສະໜອງ", {
      size: 180,
    }),
    createSortableColumn<PurchaseOrderListRow>("branchCode", "Hub", {
      size: 80,
    }),
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <PurchaseOrderStatusBadge
          status={row.original.status as PurchaseOrderStatus}
        />
      ),
    },
    createSortableColumn<PurchaseOrderListRow>("orderDate", "ວັນທີສັ່ງ", {
      size: 110,
      cell: ({ row }) => formatDateLocal(row.original.orderDate),
    }),
    {
      id: "expectedDate",
      header: "ຄາດວ່າຮັບ",
      cell: ({ row }) =>
        row.original.expectedDate
          ? formatDateLocal(row.original.expectedDate)
          : "—",
    },
    {
      id: "subtotalLak",
      header: "ມູນຄ່າ",
      cell: ({ row }) => formatLakDisplay(row.original.subtotalLak),
    },
    {
      id: "actions",
      header: "",
      size: 48,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            nav({
              to: "/app/inventory/purchase-orders/$id",
              params: { id: row.original.id },
            })
          }
        >
          <EyeIcon className="size-4" />
          ເບິ່ງ
        </Button>
      ),
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={onPaginationChange}
      emptyMessage="ຍັງບໍ່ມີໃບສັ່ງຊື້ — ສ້າງ PO ໃໝ່ເພື່ອເລີ່ມ"
    />
  );
}
